/** Angular Imports */
import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { EnvironmentService } from '@core/services/environment/environment.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
    SocketActions,
    VideoConversionMessage,
    VideoConversionResponse,
    VideoConversionStatus,
    WebSocketConnectionState,
} from './socket.interface';

/**
 * WebSocket service for video conversion real-time updates.
 * WebSocket URL is sourced from `environment.wsUrl`, swapped per build configuration via
 * Angular fileReplacements (dev → environment.dev.ts, default → environment.ts).
 */
@Injectable({
    providedIn: 'root',
})
export class SocketService implements OnDestroy {
    /** Platform ID for SSR safety */
    private readonly platformId = inject(PLATFORM_ID);

    /** Runtime environment configuration */
    private readonly env = inject(EnvironmentService);

    /** WebSocket instance */
    private socket: WebSocket | null = null;

    /** Connection state subject */
    private connectionStateSubject = new BehaviorSubject<WebSocketConnectionState>('disconnected');

    /** Conversion complete subject - broadcasts to all subscribers */
    private conversionCompleteSubject = new Subject<VideoConversionStatus>();

    /** Queue of messages to send when connected */
    private messageQueue: VideoConversionMessage[] = [];

    /** Set of currently pending conversion UUIDs */
    private pendingConversions = new Set<string>();

    /** Subject for pending conversions updates */
    private pendingConversionsSubject = new BehaviorSubject<Set<string>>(new Set<string>());

    /** Reconnection timeout */
    private reconnectTimeout: any = null;

    /** Reconnection attempt count */
    private reconnectAttempts = 0;

    /** Maximum reconnection attempts */
    private readonly MAX_RECONNECT_ATTEMPTS = 5;

    /** Reconnection delay in ms */
    private readonly RECONNECT_DELAY = 3000;

    /**
     * @public
     * @description Observable for connection state changes
     */
    public connectionState$: Observable<WebSocketConnectionState> =
        this.connectionStateSubject.asObservable();

    /**
     * @public
     * @description Observable for conversion completion events
     */
    public conversionComplete$: Observable<VideoConversionStatus> =
        this.conversionCompleteSubject.asObservable();

    /**
     * @public
     * @description Observable for pending conversions
     */
    public pendingConversions$: Observable<Set<string>> =
        this.pendingConversionsSubject.asObservable();

    /**
     * @public
     * @description Connects to the WebSocket server
     * @returns void
     */
    public connect(): void {
        // Only connect in browser environment
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const wsUrl = this.env.config.wsUrl;
        if (!wsUrl) {
            return;
        }

        // Don't reconnect if already connected or connecting
        if (
            this.socket &&
            (this.socket.readyState === WebSocket.OPEN ||
                this.socket.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        this.connectionStateSubject.next('connecting');

        try {
            this.socket = new WebSocket(wsUrl);

            this.socket.onopen = () => {
                this.connectionStateSubject.next('connected');
                this.reconnectAttempts = 0;

                // Send any queued messages (e.g., a view-page action)
                this.flushMessageQueue();
            };

            this.socket.onmessage = (event: MessageEvent) => {
                this.handleMessage(event);
            };

            this.socket.onerror = () => {
                this.connectionStateSubject.next('error');
            };

            this.socket.onclose = (event: CloseEvent) => {
                this.connectionStateSubject.next('disconnected');

                // Attempt reconnection if not a manual close
                if (event.code !== 1000 && this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
                    this.scheduleReconnect();
                }
            };
        } catch (error) {
            this.connectionStateSubject.next('error');
        }
    }

    /**
     * @public
     * @description Disconnects from the WebSocket server
     * @returns void
     */
    public disconnect(): void {
        this.clearReconnectTimeout();

        if (this.socket) {
            // Close with normal closure code
            this.socket.close(1000, 'Client initiated disconnect');
            this.socket = null;
        }

        this.connectionStateSubject.next('disconnected');
    }

    /**
     * @public
     * @description Sends a page view action to the WebSocket server
     * @param page - The page identifier (e.g., 'channellibrary', 'selectcontent')
     * @returns void
     */
    public sendViewPageAction(page: string): void {
        if (!page) {
            return;
        }

        const message: VideoConversionMessage = {
            action: 'view-page',
            page: page,
        };

        // If connected, send immediately; otherwise queue it.
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.sendMessage(message);
        } else {
            this.messageQueue.push(message);
            // Ensure we're connected
            this.connect();
        }
    }

    /**
     * @public
     * @description Sends a video conversion UUID to the server
     * @param uuid - The UUID of the video to track
     * @returns void
     */
    public sendVideoConversionUuid(uuid: string): void {
        if (!uuid) return;

        const message: VideoConversionMessage = {
            action: 'send-video-conversion-uuid',
            uuid: uuid,
        };

        // Track locally
        this.pendingConversions.add(uuid);
        this.pendingConversionsSubject.next(new Set(this.pendingConversions));

        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.sendMessage(message);
        } else {
            this.messageQueue.push(message);
            this.connect();
        }
    }

    /**
     * @public
     * @description Notifies the WS gateway that a Raspberry Pi has come online so its
     * connectionId can be stored against the licenseId/serialKey.
     *
     * @param licenseId - UUID of the license assigned to the device
     * @param serialKey - Serial key of the device
     */
    public sendRaspiConnect(licenseId: string, serialKey: string): void {
        if (!licenseId || !serialKey) return;

        this.queueOrSend({
            action: SocketActions.RASPI_CONNECT,
            licenseId,
            serialKey,
        });
    }

    /**
     * @public
     * @description Triggers a reboot on the Raspberry Pi mapped to the given licenseId.
     *
     * @param licenseId - UUID of the license assigned to the device to reboot
     */
    public sendRaspiReboot(licenseId: string): void {
        if (!licenseId) return;

        this.queueOrSend({
            action: SocketActions.RASPI_REBOOT,
            licenseId,
        });
    }

    /**
     * @public
     * @description Tells the Raspberry Pi mapped to the licenseId to pull its updated
     * content/playlist payload.
     *
     * @param licenseId - UUID of the license assigned to the device to refresh
     */
    public sendRaspiContentsUpdate(licenseId: string): void {
        if (!licenseId) return;

        this.queueOrSend({
            action: SocketActions.RASPI_CONTENTS_UPDATE,
            licenseId,
        });
    }

    /**
     * @public
     * @description Manually marks a conversion as complete (for timeout scenarios)
     * @param uuid - The UUID to mark complete
     */
    public markConversionComplete(uuid: string, success: boolean = true): void {
        // This method is now effectively deprecated as pending conversions are no longer client-tracked by UUID
        // For now, it just broadcasts the completion, but does not remove from a local set.
        // Components should react to `conversionComplete$` to refresh their UI.
        this.conversionCompleteSubject.next({
            uuid,
            success,
            timestamp: Date.now(),
        });
    }

    /**
     * @public
     * @description Manually removes a UUID or ID from the pending conversions set
     * @param uuidOrId - The UUID or database ID to remove
     */
    public removePendingConversion(uuidOrId: string): void {
        if (this.pendingConversions.has(uuidOrId)) {
            this.pendingConversions.delete(uuidOrId);
            this.pendingConversionsSubject.next(new Set(this.pendingConversions));
        }
    }

    /**
     * @private
     * @description Handles incoming WebSocket messages
     * @param event - MessageEvent containing the server response
     */
    private handleMessage(event: MessageEvent): void {
        try {
            const data: VideoConversionResponse & { id?: string; status?: string } = JSON.parse(
                event.data,
            );

            // The server is expected to send back a single message per converted item,
            // with 'Success: true' and the 'uuid' or 'ContentId' of the converted content.
            const isSuccess =
                data.Success === true ||
                data.success === true ||
                data.IsConverted === true ||
                data.isConverted === true ||
                data.status === 'completed';

            const contentId = data.uuid ?? data.ContentId ?? data.contentId ?? data.id ?? '';

            if (isSuccess && contentId) {
                // Remove from pending set
                if (this.pendingConversions.has(contentId)) {
                    this.pendingConversions.delete(contentId);
                    this.pendingConversionsSubject.next(new Set(this.pendingConversions));
                }

                // Broadcast completion; uuid holds the ContentId for components to reload that content
                this.conversionCompleteSubject.next({
                    uuid: contentId,
                    success: true,
                    timestamp: Date.now(),
                });
            } else if (isSuccess) {
                this.conversionCompleteSubject.next({
                    uuid: '', // Empty UUID signals generic success
                    success: true,
                    timestamp: Date.now(),
                });
            }
        } catch {
            // Swallow parse errors
        }
    }

    /**
     * @private
     * @description Sends if the socket is open, otherwise queues and triggers a connect.
     * Consolidates the open/queue branching shared by all public senders.
     *
     * @param message - The message to send
     */
    private queueOrSend(message: VideoConversionMessage): void {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.sendMessage(message);
        } else {
            this.messageQueue.push(message);
            this.connect();
        }
    }

    /**
     * @private
     * @description Sends a message through the WebSocket
     * @param message - The message to send
     */
    private sendMessage(message: VideoConversionMessage): void {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            this.messageQueue.push(message);
            return;
        }

        try {
            const messageStr = JSON.stringify(message);
            this.socket.send(messageStr);
        } catch {
            this.messageQueue.push(message);
        }
    }

    /**
     * @private
     * @description Flushes the message queue after connection
     */
    private flushMessageQueue(): void {
        while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            if (message) {
                this.sendMessage(message);
            }
        }
    }

    /**
     * @private
     * @description Schedules a reconnection attempt
     */
    private scheduleReconnect(): void {
        this.clearReconnectTimeout();

        this.reconnectAttempts++;
        const delay = this.RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts - 1);

        this.reconnectTimeout = setTimeout(() => {
            this.connect();
        }, delay);
    }

    /**
     * @private
     * @description Clears the reconnection timeout
     */
    private clearReconnectTimeout(): void {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
    }

    /**
     * @description Cleanup on service destroy
     */
    ngOnDestroy(): void {
        this.disconnect();
        this.conversionCompleteSubject.complete();
        this.connectionStateSubject.complete();
    }
}
